import { AnyAction } from "redux";

export interface InsightStep {
  step: string;
  status: "Pending" | "In Progress" | "Completed" | "Failed";
  messages?: string[];
}

export interface MatterInsightJob {
  insightId: string;
  correlationId: string;
  isConnecting: boolean;
  isInsightGenerating: boolean;
  status: "Queued" | "In Progress" | "Completed" | "Failed" | "Cancelled";
  progress: InsightStep[];
  serviceIcon?: string;
  insights: { [key: string]: any };
  content?: string;
  version?: number;
  finalStatus?: string;
  citations?: string[];
  justifications?: { [key: string]: string };
  createdDate?: string;
  owner?: string;
}

interface MatterInsightState {
  jobs: {
    [matterId: string]: {
      [insightId: number]: MatterInsightJob;
    };
  };
}

const blankMatterInsightJob = (insightId: string, correlationId: string): MatterInsightJob => ({
  insightId,
  correlationId,
  isConnecting: false,
  isInsightGenerating: false,
  status: "Queued",
  progress: [],
  insights: [],
  citations: [],
  justifications: {},
  createdDate: "",
  owner: "internal",
});

const mergeStep = (progress: InsightStep[], patch: InsightStep): InsightStep[] => {
  const idx = progress.findIndex((s) => s.step === patch.step);
  if (idx >= 0) {
    const existing = progress[idx];
    const updated = {
      ...existing,
      status: patch.status,
      messages: [...(existing.messages || []), ...(patch.messages || [])],
    };
    return [...progress.slice(0, idx), updated, ...progress.slice(idx + 1)];
  }
  return [...progress, patch];
};

const initialState: MatterInsightState = {
  jobs: {},
};

export const matterInsightReducer = (state = initialState, action: AnyAction): MatterInsightState => {
  const matterId = action.payload?.matterId;
  const insightId = action.payload?.insightId;
  const correlationId = action.payload?.correlationId;

  if (!matterId || !insightId) return state;

  const existingJob: MatterInsightJob =
    state.jobs?.[matterId]?.[insightId] ?? blankMatterInsightJob(insightId.toString(), correlationId || "");

  const baseJob: MatterInsightJob = correlationId
    ? { ...existingJob, correlationId }
    : existingJob;

  switch (action.type) {
    case "MATTER_INSIGHT_JOB_CONNECTING":
      return {
        jobs: {
          ...state.jobs,
          [matterId]: {
            ...(state.jobs[matterId] || {}),
            [insightId]: { ...baseJob, isConnecting: true },
          },
        },
      };

    case "MATTER_INSIGHT_JOB_STARTED":
      return {
        jobs: {
          ...state.jobs,
          [matterId]: {
            ...(state.jobs[matterId] || {}),
            [insightId]: {
              ...baseJob,
              isConnecting: false,
              isInsightGenerating: true,
              status: "In Progress",
              progress: action.payload.progress || [],
              serviceIcon: action.payload.service_icon,
            },
          },
        },
      };

    case "MATTER_INSIGHT_PROGRESS_UPDATE": {
      const newProgress = Array.isArray(action.payload.progress)
        ? action.payload.progress
        : mergeStep(baseJob.progress, action.payload);

      return {
        jobs: {
          ...state.jobs,
          [matterId]: {
            ...(state.jobs[matterId] || {}),
            [insightId]: {
              ...baseJob,
              isInsightGenerating: true,
              progress: newProgress,
              serviceIcon: action.payload.service_icon || baseJob.serviceIcon,
            },
          },
        },
      };
    }

    case "MATTER_INSIGHT_GENERATION_COMPLETE": {
      const { final } = action.payload;
      return {
        jobs: {
          ...state.jobs,
          [matterId]: {
            ...(state.jobs[matterId] || {}),
            [insightId]: {
              ...baseJob,
              isInsightGenerating: false,
              isConnecting: false,
              status: "Completed",
              content: final?.generated_data?.html_insight,
              version: final?.version,
              citations: final?.citations,
              justifications: final?.justifications,
              createdDate: final?.createdDate,
              owner: final?.owner,
              progress: action.payload.progress || baseJob.progress,
            },
          },
        },
      };
    }

    case "MATTER_INSIGHT_GENERATION_FAILED":
      return {
        jobs: {
          ...state.jobs,
          [matterId]: {
            ...(state.jobs[matterId] || {}),
            [insightId]: {
              ...baseJob,
              isInsightGenerating: false,
              isConnecting: false,
              status: "Failed",
            },
          },
        },
      };

    case "MATTER_INSIGHT_CANCELLED":
      return {
        jobs: {
          ...state.jobs,
          [matterId]: {
            ...(state.jobs[matterId] || {}),
            [insightId]: {
              ...baseJob,
              isInsightGenerating: false,
              isConnecting: false,
              status: "Cancelled",
            },
          },
        },
      };

    case "UPDATE_MATTER_INSIGHT": {
      const { insightId, content, version, entityId, status, citations, justifications, createdDate, owner } = action.payload;
      const isComplete = Boolean(content && version);
      const existingCorrelationId = localStorage.getItem(`matter_insight:correlation:${entityId}:${insightId}`);
      const isInsightGenerating = !isComplete && Boolean(existingCorrelationId);

      const current = state.jobs?.[entityId]?.[insightId] ?? blankMatterInsightJob(insightId.toString(), existingCorrelationId || "");

      return {
        jobs: {
          ...state.jobs,
          [entityId]: {
            ...(state.jobs[entityId] || {}),
            [insightId]: {
              ...current,
              insightId,
              content,
              version,
              citations,
              justifications,
              createdDate,
              owner,
              finalStatus: status || current.finalStatus,
              isInsightGenerating,
              status: isComplete ? "Completed" : current.status,
            },
          },
        },
      };
    }

    case "UPDATE_MATTER_INSIGHT_STATUS":
      return {
        jobs: {
          ...state.jobs,
          [matterId]: {
            ...(state.jobs[matterId] || {}),
            [insightId]: {
              ...baseJob,
              finalStatus: action.payload.status,
            },
          },
        },
      };

    default:
      return state;
  }
};